import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";

import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Loader from "shared/Icons/Loader/Loader";
import { SecondaryButton } from "widgets";
import routes from "constants/routes";
import { VIEW_DASH } from "constants/schoolConstants";
import {
  formatDisplayValue,
  formatGradeLabel,
  useBranchDetailsLookups,
} from "helpers/tenantUtils";

import styles from "./ViewBranchDetails.module.scss";

const dash = VIEW_DASH;

const handleOnClickCreateBranchButton = (
  navigate,
  tenantId,
  tenantName,
  tenantCode,
  gradeList,
) => {
  navigate(`/createBranch?tenantId=${tenantId}`, {
    state: {
      tenantName,
      tenantCode,
      gradeList,
    },
  });
};

const Section = ({ icon: Icon, title, children }) => (
  <section className={styles.section}>
    <div className={styles.sectionHeader}>
      {Icon ? <Icon className={styles.sectionIcon} aria-hidden /> : null}
      <h3 className={styles.sectionTitle}>{title}</h3>
    </div>
    <div className={styles.sectionBody}>{children}</div>
  </section>
);

const Field = ({ label, value, fullWidth, isLoading }) => (
  <div className={`${styles.field} ${fullWidth ? styles.fieldFull : ""}`}>
    <div className={styles.fieldLabel}>{label}</div>
    <div className={styles.fieldValue}>
      {isLoading ? "Loading…" : formatDisplayValue(value)}
    </div>
  </div>
);

const FieldGrid = ({ children }) => (
  <div className={styles.fieldGrid}>{children}</div>
);

const copyTextToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "absolute";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
};

const BranchIdField = ({ branchId }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!branchId) return;
    try {
      await copyTextToClipboard(branchId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [branchId]);

  return (
    <div className={`${styles.field} ${styles.fieldFull}`}>
      <div className={styles.fieldLabel}>Branch ID</div>
      <div className={styles.branchIdRow}>
        <span className={styles.branchIdValue}>
          {formatDisplayValue(branchId)}
        </span>
        <button
          type="button"
          className={styles.copyIdButton}
          onClick={handleCopy}
          disabled={!branchId}
          aria-label="Copy branch ID to clipboard"
        >
          <ContentCopyOutlinedIcon className={styles.copyIdIcon} aria-hidden />
        </button>
        {copied ? (
          <span className={styles.copiedBadge} role="status">
            Copied
          </span>
        ) : null}
      </div>
    </div>
  );
};

const ViewBranchDetails = ({
  branchDetails,
  allowSingleBranch = false,
  gradeList = [],
  tenantCountryId,
}) => {
  const navigate = useNavigate();
  const {
    lookups,
    address,
    isCountryListLoading,
    isPostalLookupLoading,
    isPostalError,
    shouldFetchPostal,
  } = useBranchDetailsLookups(branchDetails, tenantCountryId);

  if (!branchDetails) return null;

  const taxRegistrationNumber =
    branchDetails.taxRegistrationNumber ||
    branchDetails.tax_registration_number;

  const tenantDetailsUrl = branchDetails.tenantId
    ? `${routes.tenantDetails.tenantDetails}?tenantId=${branchDetails.tenantId}`
    : null;

  return (
    <div className={styles.pageCard}>
      <header className={styles.hero}>
        <div className={styles.heroMain}>
          <div className={styles.logoMark} aria-hidden>
            <AccountTreeOutlinedIcon className={styles.logoMarkIcon} />
          </div>
          <div className={styles.heroText}>
            <h2 className={styles.heroTitle}>{branchDetails.branchName}</h2>
            <div className={styles.heroMeta}>
              <span className={styles.codeChip}>
                Branch {branchDetails.branchCode}
              </span>
              {branchDetails.tenantCode ? (
                <span className={styles.tenantChip}>
                  Tenant {branchDetails.tenantCode}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        {!allowSingleBranch ? (
          <SecondaryButton
            onClick={() =>
              handleOnClickCreateBranchButton(
                navigate,
                branchDetails.tenantId,
                branchDetails.tenantName,
                branchDetails.tenantCode,
                gradeList,
              )
            }
          >
            Create branch
          </SecondaryButton>
        ) : null}
      </header>

      <Section icon={InfoOutlinedIcon} title="Branch information">
        <FieldGrid>
          <BranchIdField branchId={branchDetails.id} />
          <Field label="Branch code" value={branchDetails.branchCode} />
          <Field label="Tenant name" value={branchDetails.tenantName} />
          <Field
            label="Tenant code"
            value={
              tenantDetailsUrl ? (
                <Link to={tenantDetailsUrl} className={styles.link}>
                  {branchDetails.tenantCode}
                </Link>
              ) : (
                branchDetails.tenantCode
              )
            }
          />
          <Field label="Created by" value={branchDetails.createdBy} />
        </FieldGrid>
      </Section>

      <Section icon={PersonOutlineOutlinedIcon} title="Contact">
        <FieldGrid>
          <Field label="Name" value={branchDetails.contact?.name} />
          <Field label="Phone" value={branchDetails.contact?.phone} />
          <Field
            label="ISD code"
            value={lookups.isdCode?.value}
            isLoading={lookups.isdCode?.isLoading || isCountryListLoading}
          />
          <Field
            label="Designation"
            value={branchDetails.contact?.designation}
          />
        </FieldGrid>
      </Section>

      <Section icon={MailOutlineOutlinedIcon} title="Communication">
        <FieldGrid>
          <Field label="Email" value={branchDetails.email} />
          <Field
            label="Ambassador email"
            value={branchDetails.ambassadorMail}
          />
          {lookups.isIndia ? (
            <Field label="GSTIN" value={branchDetails.gstin} />
          ) : (
            <Field
              label="Tax registration number"
              value={taxRegistrationNumber}
            />
          )}
        </FieldGrid>
      </Section>

      <Section icon={LocationOnOutlinedIcon} title="Address">
        {shouldFetchPostal && isPostalLookupLoading ? (
          <div className={styles.inlineLoader}>
            <Loader />
            <span>Loading location details…</span>
          </div>
        ) : null}
        {isPostalError ? (
          <p className={styles.softWarning} role="status">
            Location lookup unavailable. Showing saved address fields only.
          </p>
        ) : null}
        <FieldGrid>
          <Field
            label="Address line 1"
            fullWidth
            value={address.addressLine1}
          />
          <Field
            label="Address line 2"
            fullWidth
            value={address.addressLine2}
          />
          <Field label="Street" value={address.street} />
          <Field label="City" value={address.city} />
          <Field
            label="District"
            value={lookups.district?.value}
            isLoading={lookups.district?.isLoading}
          />
          <Field
            label="State"
            value={lookups.state?.value}
            isLoading={lookups.state?.isLoading}
          />
          <Field label="PIN code" value={address.pinCode} />
          <Field
            label="Area"
            value={lookups.isIndia ? null : address.area}
          />
        </FieldGrid>
      </Section>

      <Section icon={SchoolOutlinedIcon} title="Academic">
        <FieldGrid>
          <div className={`${styles.field} ${styles.gradesField}`}>
            <div className={styles.gradesLabel}>Grades</div>
            <div className={styles.tagList}>
              {branchDetails.gradeIds?.length ? (
                branchDetails.gradeIds.map((gradeId) => (
                  <span key={gradeId} className={styles.gradeTag}>
                    {formatGradeLabel(gradeId)}
                  </span>
                ))
              ) : (
                <span className={styles.fieldValueMuted}>{dash}</span>
              )}
            </div>
          </div>
        </FieldGrid>
      </Section>
    </div>
  );
};

export default ViewBranchDetails;
